import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Landmark, Copy, Home, Loader2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VirementPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [bankDetails, setBankDetails] = useState({ iban: '', account_name: '', rib_url: '' });
  const [orderReference, setOrderReference] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bank details
        const bankResponse = await axios.get(`${API}/bank-details`);
        setBankDetails(bankResponse.data);
        
        // Fetch order to get customer name for reference
        if (orderId) {
          try {
            const orderResponse = await axios.get(`${API}/orders/${orderId}`);
            const customer = orderResponse.data.customer;
            if (customer && customer.prenom && customer.nom) {
              setOrderReference(`${customer.prenom} ${customer.nom}`);
            } else {
              setOrderReference(orderId);
            }
          } catch (orderError) {
            // Error handled silently
            setOrderReference(orderId);
          }
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orderId]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copié !`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="virement-page">
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 mx-auto bg-secondary flex items-center justify-center mb-6">
            <Landmark className="w-10 h-10" />
          </div>
          <h1 className="font-heading font-black text-3xl md:text-4xl uppercase tracking-tight">
            Paiement par virement
          </h1>
          <p className="font-body text-muted-foreground mt-4">
            Effectuez votre virement avec les informations ci-dessous. Votre commande sera validée dès réception.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="border border-border p-8 space-y-6"
        >
          <div className="flex justify-between items-start">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Bénéficiaire
              </span>
              <p className="font-heading font-bold text-lg mt-1">{bankDetails.account_name || 'Non configuré'}</p>
            </div>
            {bankDetails.account_name && (
              <button
                onClick={() => copyToClipboard(bankDetails.account_name, 'Bénéficiaire')}
                className="p-2 hover:bg-secondary transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex justify-between items-start">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                IBAN
              </span>
              <p className="font-mono font-bold text-lg mt-1">{bankDetails.iban || 'Non configuré'}</p>
            </div>
            {bankDetails.iban && (
              <button
                onClick={() => copyToClipboard(bankDetails.iban.replace(/\s/g, ''), 'IBAN')}
                className="p-2 hover:bg-secondary transition-colors"
                data-testid="copy-iban"
              >
                <Copy className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Download RIB Button */}
          {bankDetails.rib_url && (
            <div className="pt-4 border-t border-border">
              <a 
                href={bankDetails.rib_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-3 bg-[#0047AB] text-white hover:bg-[#003380] transition-colors font-mono text-sm uppercase"
                data-testid="download-rib"
              >
                <Download className="w-4 h-4" />
                Télécharger le RIB (PDF)
              </a>
            </div>
          )}

          <div className="border-t border-border pt-6">
            <div className="flex justify-between items-start">
              <div>
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Référence de commande (à indiquer)
                </span>
                <p className="font-mono font-bold text-xl mt-1 text-[#0047AB]">{orderReference || 'N/A'}</p>
              </div>
              {orderReference && (
                <button
                  onClick={() => copyToClipboard(orderReference, 'Référence')}
                  className="p-2 hover:bg-secondary transition-colors"
                  data-testid="copy-reference"
                >
                  <Copy className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 p-6 bg-secondary"
        >
          <h3 className="font-heading font-bold uppercase mb-2">Important</h3>
          <ul className="font-body text-sm text-muted-foreground space-y-2">
            <li>• Indiquez bien la référence de commande dans le libellé du virement</li>
            <li>• Votre commande sera validée sous 24-48h après réception du paiement</li>
            <li>• Vous recevrez un email de confirmation une fois le paiement validé</li>
          </ul>
        </motion.div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button className="h-12 px-8 rounded-none font-mono uppercase tracking-widest" data-testid="return-home">
              <Home className="mr-2 w-4 h-4" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
